import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, reactive, ref, watch } from "vue";
  import { useStore } from "vuex";
  import projectPageApi, {
    ProjectPageData,
  } from "@/services/api/projectPageApi";
  import LandSubdivisionProjectPageEditor from "@/v2/views/project-page/LandSubdivisionProjectPageEditor.vue";
  import { EstateType } from "@/enums/EstateType";

  type ModalType = "hero" | "content" | "buttons" | null;
  
export default /*@__PURE__*/_defineComponent({
  __name: 'ProjectPageView',
  setup(__props, { expose: __expose }) {
  __expose();

  const store = useStore();

  const estateId = computed<string>(() => store.state.user.selectedEstateId);
  const selectedEstate = computed(() =>
    store.state.user.landownerEstates.find(
      (e: { _id: string }) => e._id === store.state.user.selectedEstateId
    )
  );
  const isLandSubdivision = computed(
    () => selectedEstate.value?.type === EstateType.LAND_SUBDIVISION
  );
  const estateType = computed<string>(
    () => selectedEstate.value?.appVersion ?? "v2"
  );

  const pageData = reactive<ProjectPageData>({
    backgroundImageUrl: null,
    residentialContent: {
      brandText: "",
      headline: "",
      description: "",
      actionButtons: [],
      logoImageUrl: undefined,
    },
  });

  const loading = ref(false);

  async function fetchPageData() {
    if (!estateId.value) return;

    try {
      loading.value = true;
      const { data } = await projectPageApi.getByEstateId(estateId.value);

      Object.assign(pageData, data);
    } catch (error: any) {
      if (error?.response?.status !== 404) {
        console.error("Failed to fetch project page data:", error);
      }
    } finally {
      loading.value = false;
    }
  }

  onMounted(fetchPageData);
  watch(estateId, fetchPageData);

  const slug = computed(() => {
    const text = selectedEstate.value?.title || "project";

    return text
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  });

  const fullUrl = computed(() => {
    const base = process.env.VUE_APP_WEB_URL || window.location.origin;

    return `${base}/residential-communities/${slug.value}`;
  });

  const copied = ref(false);

  async function handleCopy(url: string) {
    try {
      await navigator.clipboard.writeText(url);
      copied.value = true;
      setTimeout(() => (copied.value = false), 2000);
    } catch {
      console.warn("Clipboard API unavailable, could not copy URL");
    }
  }

  const heroBgStyle = computed(() => {
    if (heroPreview.value) {
      return { backgroundImage: `url(${heroPreview.value})` };
    }

    if (pageData.backgroundImageUrl) {
      return { backgroundImage: `url(${pageData.backgroundImageUrl})` };
    }

    return { backgroundColor: "#2d3a3a" };
  });

  const visibleButtons = computed(() =>
    (pageData.residentialContent?.actionButtons ?? []).filter((b) => b.label)
  );

  const BUTTON_ICONS: Record<string, string> = {
    home: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>`,
    map: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polygon points="1 6 1 22 8 18 16 22 23 18 23 2 16 6 8 2 1 6"/><line x1="8" y1="2" x2="8" y2="18"/><line x1="16" y1="6" x2="16" y2="22"/></svg>`,
    document: `<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>`,
  };

  function getButtonIcon(destination: string) {
    return BUTTON_ICONS[destination] ?? BUTTON_ICONS.document;
  }

  const activeModal = ref<ModalType>(null);
  const saving = ref(false);

  const heroFileRef = ref<HTMLInputElement | null>(null);
  const heroPreview = ref<string | null>(null);

  const logoFileRef = ref<HTMLInputElement | null>(null);
  const logoPreview = ref<string | null>(null);
  const form = reactive({
    brandText: "",
    headline: "",
    description: "",
  });

  const formButtons = reactive([
    { label: "", destination: "home" as const },
    { label: "", destination: "map" as const },
    { label: "", destination: "document" as const },
  ]);

  function openModal(type: ModalType) {
    if (type === "content") {
      form.brandText = pageData.residentialContent?.brandText ?? "";
      form.headline = pageData.residentialContent?.headline ?? "";
      form.description = pageData.residentialContent?.description ?? "";
      logoPreview.value = null;
    }

    if (type === "buttons") {
      const existing = pageData.residentialContent?.actionButtons ?? [];
      const destinations = ["home", "map", "document"] as const;

      destinations.forEach((dest, i) => {
        const match = existing.find((b) => b.destination === dest);

        formButtons[i].label = match?.label ?? "";
        formButtons[i].destination = dest;
      });
    }

    heroPreview.value = null;
    activeModal.value = type;
  }

  function closeModal() {
    activeModal.value = null;
    heroPreview.value = null;
    logoPreview.value = null;
  }

  function onHeroFileChange() {
    const file = heroFileRef.value?.files?.[0];

    if (file) heroPreview.value = URL.createObjectURL(file);
  }

  function onLogoFileChange() {
    const file = logoFileRef.value?.files?.[0];

    if (file) logoPreview.value = URL.createObjectURL(file);
  }

  async function removeLogo() {
    if (!estateId.value) return;

    try {
      saving.value = true;
      logoPreview.value = null;

      if (logoFileRef.value) logoFileRef.value.value = "";

      const existing = pageData.residentialContent?.actionButtons ?? [];
      const { data } = await projectPageApi.upsert(
        estateId.value,
        estateType.value,
        {
          residentialContent: {
            brandText: form.brandText,
            headline: form.headline,
            description: form.description,
            actionButtons: existing,
            logoImageUrl: "",
          },
        }
      );

      Object.assign(pageData, data);
    } finally {
      saving.value = false;
    }
  }

  async function saveHero() {
    const file = heroFileRef.value?.files?.[0];

    if (!file || !estateId.value) {
      closeModal();
      return;
    }

    try {
      saving.value = true;
      const { data } = await projectPageApi.uploadBackground(
        estateId.value,
        estateType.value,
        file
      );

      pageData.backgroundImageUrl = data.backgroundImageUrl;
      closeModal();
    } finally {
      saving.value = false;
    }
  }

  async function saveContent() {
    if (!estateId.value) return;

    try {
      saving.value = true;
      const logoFile = logoFileRef.value?.files?.[0];
      let logoImageUrl = pageData.residentialContent?.logoImageUrl;

      if (logoFile) {
        const { data } = await projectPageApi.uploadLogo(
          estateId.value,
          estateType.value,
          logoFile
        );

        logoImageUrl = data.residentialContent?.logoImageUrl;
      }

      const existing = pageData.residentialContent?.actionButtons ?? [];
      const { data } = await projectPageApi.upsert(
        estateId.value,
        estateType.value,
        {
          residentialContent: {
            brandText: form.brandText,
            headline: form.headline,
            description: form.description,
            actionButtons: existing,
            ...(logoImageUrl !== undefined && { logoImageUrl }),
          },
        }
      );

      Object.assign(pageData, data);
      closeModal();
    } finally {
      saving.value = false;
    }
  }

  async function saveButtons() {
    if (!estateId.value) return;

    try {
      saving.value = true;
      const actionButtons = formButtons
        .filter((b) => b.label.trim())
        .map((b) => ({ label: b.label, destination: b.destination }));

      const { data } = await projectPageApi.upsert(
        estateId.value,
        estateType.value,
        {
          residentialContent: {
            ...(pageData.residentialContent ?? {}),
            actionButtons,
          },
        }
      );

      Object.assign(pageData, data);
      closeModal();
    } finally {
      saving.value = false;
    }
  }

const __returned__ = { store, estateId, selectedEstate, isLandSubdivision, estateType, pageData, loading, fetchPageData, slug, fullUrl, copied, handleCopy, heroBgStyle, visibleButtons, BUTTON_ICONS, getButtonIcon, activeModal, saving, heroFileRef, heroPreview, logoFileRef, logoPreview, form, formButtons, openModal, closeModal, onHeroFileChange, onLogoFileChange, removeLogo, saveHero, saveContent, saveButtons, computed, onMounted, reactive, ref, watch, get useStore() { return useStore }, get projectPageApi() { return projectPageApi }, get ProjectPageData() { return ProjectPageData }, LandSubdivisionProjectPageEditor, get EstateType() { return EstateType } }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})